import { useEffect, useRef, useState } from "react";
import { APIProvider, Map, useMap, useMapsLibrary } from "@vis.gl/react-google-maps";
import { Pencil, RotateCcw, Check, Search, Loader2 } from "lucide-react";
import { placesAutocomplete, geocode } from "../lib/api";

const DEFAULT_CENTER = { lat: 51.8787, lng: -0.42 }; // Luton

/* Custom address search using our backend (Places New + Geocoding) */
function SearchBox({ onPlaceSelected }) {
  const map = useMap();
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const debounceRef = useRef(null);
  const skipRef = useRef(false);
  const markerRef = useRef(null);

  const goTo = (lat, lng) => {
    if (!map) return;
    map.setCenter({ lat, lng });
    map.setZoom(20);
    if (!markerRef.current && window.google) {
      markerRef.current = new window.google.maps.Marker({ map });
    }
    if (markerRef.current) markerRef.current.setPosition({ lat, lng });
  };

  useEffect(() => {
    if (skipRef.current) {
      skipRef.current = false;
      setSuggestions([]);
      setOpen(false);
      return;
    }
    if (q.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await placesAutocomplete(q);
        setSuggestions(res.suggestions || []);
        setOpen(true);
      } catch {
        setSuggestions([]);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q]);

  const select = async (s) => {
    skipRef.current = true;
    setQ(s.description);
    setSuggestions([]);
    setOpen(false);
    setLoading(true);
    try {
      const g = await geocode(s.placeId ? { place_id: s.placeId } : { q: s.description });
      if (g.found && map) {
        goTo(g.lat, g.lng);
        onPlaceSelected && onPlaceSelected(g.lat, g.lng, g.formatted_address || s.description);
      }
    } finally {
      setLoading(false);
    }
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (suggestions.length > 0) return select(suggestions[0]);
    if (q.trim().length < 3) return;
    setLoading(true);
    setOpen(false);
    try {
      const g = await geocode({ q });
      if (g.found && map) {
        goTo(g.lat, g.lng);
        onPlaceSelected && onPlaceSelected(g.lat, g.lng, g.formatted_address || q);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="absolute top-4 left-4 right-4 z-10 max-w-md">
      <form onSubmit={onSubmit} className="relative">
        {loading ? (
          <Loader2 className="w-4 h-4 text-brand-accent absolute left-3.5 top-1/2 -translate-y-1/2 animate-spin z-10" />
        ) : (
          <Search className="w-4 h-4 text-stone-400 absolute left-3.5 top-1/2 -translate-y-1/2 z-10" />
        )}
        <input
          data-testid="roof-address-search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onFocus={() => suggestions.length && setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          placeholder="Search your address…"
          className="w-full rounded-xl border border-stone-300 bg-white pl-10 pr-4 py-3 shadow-lg font-figtree text-sm outline-none focus:ring-2 focus:ring-brand-accent"
          autoComplete="off"
        />
      </form>
      {open && suggestions.length > 0 && (
        <ul data-testid="address-suggestions" className="mt-1 bg-white rounded-xl shadow-xl border border-stone-200 overflow-hidden max-h-64 overflow-y-auto">
          {suggestions.map((s, i) => (
            <li key={s.placeId || i}>
              <button
                type="button"
                data-testid={`address-suggestion-${i}`}
                onMouseDown={(e) => { e.preventDefault(); select(s); }}
                onTouchStart={(e) => { e.preventDefault(); select(s); }}
                className="w-full text-left px-4 py-2.5 text-sm font-figtree text-brand hover:bg-brand-cream transition-colors"
              >
                {s.description}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/* Custom click-to-draw polygon (DrawingManager removed in Maps v3.65) */
function DrawingLayer({ onArea }) {
  const map = useMap();
  const geometry = useMapsLibrary("geometry");
  const polyRef = useRef(null);
  const [points, setPoints] = useState([]);
  const [drawMode, setDrawMode] = useState(false);

  // create the polygon once
  useEffect(() => {
    if (!map || !window.google) return;
    const poly = new window.google.maps.Polygon({
      map,
      fillColor: "#D96C4A",
      fillOpacity: 0.35,
      strokeColor: "#D96C4A",
      strokeWeight: 2,
      clickable: false,
    });
    polyRef.current = poly;
    return () => poly.setMap(null);
  }, [map]);

  // sync path + area whenever points change
  useEffect(() => {
    if (!polyRef.current) return;
    polyRef.current.setPath(points);
    if (points.length >= 3 && geometry) {
      const latLngs = points.map((p) => new window.google.maps.LatLng(p.lat, p.lng));
      const area = geometry.spherical.computeArea(latLngs);
      onArea(Math.round(area), points);
    } else {
      onArea(0, points);
    }
  }, [points, geometry]); // eslint-disable-line

  // add vertices on map click while drawing
  useEffect(() => {
    if (!map || !drawMode) return;
    map.setOptions({ draggableCursor: "crosshair" });
    const l = map.addListener("click", (e) => {
      setPoints((p) => [...p, { lat: e.latLng.lat(), lng: e.latLng.lng() }]);
    });
    return () => {
      l.remove();
      map.setOptions({ draggableCursor: null });
    };
  }, [map, drawMode]);

  const startDraw = () => {
    setPoints([]);
    setDrawMode(true);
  };
  const finishDraw = () => setDrawMode(false);
  const clearDraw = () => {
    setPoints([]);
    setDrawMode(true);
  };

  return (
    <div className="absolute bottom-4 left-4 z-10 flex gap-2">
      {!drawMode ? (
        <button type="button" data-testid="roof-draw-button" onClick={startDraw} className="bg-brand text-white rounded-full px-4 py-2.5 text-sm font-medium shadow-lg inline-flex items-center gap-2">
          <Pencil className="w-4 h-4" /> Draw roof
        </button>
      ) : (
        <button type="button" data-testid="roof-finish-button" onClick={finishDraw} className="bg-brand-success text-white rounded-full px-4 py-2.5 text-sm font-medium shadow-lg inline-flex items-center gap-2">
          <Check className="w-4 h-4" /> Finish
        </button>
      )}
      <button type="button" data-testid="roof-clear-button" onClick={clearDraw} className="bg-white text-brand border border-stone-300 rounded-full px-4 py-2.5 text-sm font-medium shadow-lg inline-flex items-center gap-2">
        <RotateCcw className="w-4 h-4" /> Redraw
      </button>
    </div>
  );
}

function DrawHint({ area }) {
  return (
    <div className="absolute top-4 right-4 z-10 bg-white/95 backdrop-blur rounded-xl px-4 py-2 shadow-lg border border-stone-200">
      <p className="font-figtree text-xs text-stone-500">Roof area</p>
      <p className="font-outfit font-bold text-brand" data-testid="map-area-readout">{area ? `${area} m²` : "—"}</p>
    </div>
  );
}

export default function RoofMap({ apiKey, onArea, area, onPlaceSelected }) {
  return (
    <APIProvider apiKey={apiKey} libraries={["places", "geometry"]}>
      <div className="relative w-full h-[440px] rounded-2xl overflow-hidden border border-stone-200">
        <Map
          defaultCenter={DEFAULT_CENTER}
          defaultZoom={19}
          mapTypeId="satellite"
          tilt={0}
          gestureHandling="greedy"
          disableDefaultUI={true}
          zoomControl={true}
          style={{ width: "100%", height: "100%" }}
        >
          <SearchBox onPlaceSelected={onPlaceSelected} />
          <DrawHint area={area} />
          <DrawingLayer onArea={onArea} />
        </Map>
      </div>
      <p className="font-figtree text-sm text-stone-500 mt-3">
        Search your address — we'll auto-detect your roof from satellite data. You can also tap <b>Draw roof</b> to outline it yourself for a manual measurement.
      </p>
    </APIProvider>
  );
}
